// src/lib/resellerLevels.ts
// Helper murni untuk fitur "Auto Naik Level Reseller" (mig 094).
// Cermin TS dari logika SQL evaluate_reseller_level / compute_reseller_metrics
// untuk keperluan tampilan (editor aturan di admin + progress di akun reseller).
// TANPA dependensi Supabase → mudah diuji.

export type PromoteWindow =
  | "all_time"
  | "calendar_month"
  | "calendar_year"
  | "rolling_days";

export type PromoteMatch = "all" | "any";

/** Aturan naik level untuk satu grup (slot > 1). */
export type LevelRule = {
  autoPromote: boolean;
  match: PromoteMatch;
  minSpending: number; // Rp, 0 = abaikan
  minOrders: number; // kali, 0 = abaikan
  window: PromoteWindow;
  windowDays: number; // dipakai saat window = rolling_days
};

export const DEFAULT_LEVEL_RULE: LevelRule = {
  autoPromote: false,
  match: "all",
  minSpending: 0,
  minOrders: 0,
  window: "all_time",
  windowDays: 30,
};

export const WINDOW_OPTIONS: { value: PromoteWindow; label: string }[] = [
  { value: "all_time", label: "Sepanjang waktu" },
  { value: "calendar_month", label: "Bulan berjalan" },
  { value: "calendar_year", label: "Tahun berjalan" },
  { value: "rolling_days", label: "N hari terakhir" },
];

export const MATCH_OPTIONS: { value: PromoteMatch; label: string }[] = [
  { value: "all", label: "Penuhi semua syarat" },
  { value: "any", label: "Cukup salah satu" },
];

export function formatRupiah(n: number): string {
  return "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
}

/** Label periode singkat untuk kalimat ("dalam ..."). */
export function windowLabel(window: PromoteWindow, days = 30): string {
  switch (window) {
    case "calendar_month":
      return "bulan ini";
    case "calendar_year":
      return "tahun ini";
    case "rolling_days":
      return `${Math.max(1, Math.trunc(days) || 1)} hari terakhir`;
    case "all_time":
    default:
      return "sepanjang waktu";
  }
}

/** Deskripsi syarat dalam satu kalimat (admin/akun). */
export function describeCriteria(rule: LevelRule): string {
  if (!rule.autoPromote) return "Naik level manual (diatur admin)";
  const parts: string[] = [];
  if (rule.minSpending > 0) parts.push(`belanja ≥ ${formatRupiah(rule.minSpending)}`);
  if (rule.minOrders > 0) parts.push(`≥ ${rule.minOrders} order`);
  if (parts.length === 0) return "Belum ada syarat — atur min. belanja / order";
  const joiner = rule.match === "any" ? " atau " : " dan ";
  return `Jika ${parts.join(joiner)} (${windowLabel(rule.window, rule.windowDays)})`;
}

/** Validasi draft aturan. Return pesan error atau null bila valid. */
export function validateLevelRuleDraft(rule: LevelRule): string | null {
  if (!rule.autoPromote) return null;
  if (rule.minSpending < 0 || rule.minOrders < 0) {
    return "Nilai syarat tidak boleh negatif.";
  }
  if (rule.minSpending <= 0 && rule.minOrders <= 0) {
    return "Aktifkan minimal satu syarat: total belanja atau jumlah order.";
  }
  if (
    rule.window === "rolling_days" &&
    (!Number.isFinite(rule.windowDays) || rule.windowDays < 1)
  ) {
    return "Jumlah hari periode minimal 1.";
  }
  return null;
}

// ── Progress (halaman akun reseller) ─────────────────────────────────────────

export type LevelProgressNext = {
  slot: number;
  name: string;
  discountPersen: number;
  match: PromoteMatch;
  window: PromoteWindow;
  windowDays: number;
  minSpending: number;
  minOrders: number;
  spending: number;
  orderCount: number;
};

export type LevelProgress = {
  current: { slot: number; name: string; discountPersen: number };
  next: LevelProgressNext | null;
  isMax: boolean;
};

/** Persentase satu metrik (0..1). target ≤ 0 → tidak relevan (1). */
export function metricRatio(value: number, target: number): number {
  if (target <= 0) return 1;
  if (value <= 0) return 0;
  return Math.min(1, value / target);
}

/**
 * Progress keseluruhan menuju level berikutnya (0..1).
 * - match 'all': gating → ambil progres TERKECIL dari syarat yang diset.
 * - match 'any': cukup satu → ambil progres TERBESAR.
 */
export function overallRatio(next: LevelProgressNext): number {
  const parts: number[] = [];
  if (next.minSpending > 0) parts.push(metricRatio(next.spending, next.minSpending));
  if (next.minOrders > 0) parts.push(metricRatio(next.orderCount, next.minOrders));
  if (parts.length === 0) return 0;
  return next.match === "any" ? Math.max(...parts) : Math.min(...parts);
}

/** Apakah syarat level berikutnya sudah terpenuhi. */
export function meetsNext(next: LevelProgressNext): boolean {
  const spendOk = next.minSpending <= 0 || next.spending >= next.minSpending;
  const orderOk = next.minOrders <= 0 || next.orderCount >= next.minOrders;
  return next.match === "any"
    ? (next.minSpending > 0 && spendOk) || (next.minOrders > 0 && orderOk)
    : spendOk && orderOk;
}

/** Teks "kurang ... lagi" menuju level berikutnya. */
export function remainingText(next: LevelProgressNext): string {
  if (meetsNext(next)) return "Syarat terpenuhi 🎉";
  const needs: string[] = [];
  if (next.minSpending > 0 && next.spending < next.minSpending) {
    needs.push(`${formatRupiah(next.minSpending - next.spending)} belanja`);
  }
  if (next.minOrders > 0 && next.orderCount < next.minOrders) {
    needs.push(`${next.minOrders - next.orderCount} order`);
  }
  if (needs.length === 0) return "Syarat terpenuhi 🎉";
  const joiner = next.match === "any" ? " atau " : " & ";
  return `Kurang ${needs.join(joiner)} lagi`;
}

/** Map baris DB reseller_groups → LevelRule (koersi tipe defensif). */
export function ruleFromRow(row: {
  auto_promote?: boolean | null;
  promote_match?: string | null;
  min_spending?: number | string | null;
  min_orders?: number | string | null;
  promote_window?: string | null;
  promote_window_days?: number | string | null;
}): LevelRule {
  const match: PromoteMatch = row.promote_match === "any" ? "any" : "all";
  const window: PromoteWindow =
    row.promote_window === "calendar_month" ||
    row.promote_window === "calendar_year" ||
    row.promote_window === "rolling_days"
      ? row.promote_window
      : "all_time";
  return {
    autoPromote: !!row.auto_promote,
    match,
    minSpending: Math.max(0, Number(row.min_spending ?? 0) || 0),
    minOrders: Math.max(0, Math.trunc(Number(row.min_orders ?? 0) || 0)),
    window,
    windowDays: Math.max(1, Math.trunc(Number(row.promote_window_days ?? 30) || 30)),
  };
}

/** Map JSON dari RPC get_my_level_progress → LevelProgress (defensif). */
export function parseLevelProgress(raw: unknown): LevelProgress | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const cur = (obj.current ?? {}) as Record<string, unknown>;
  const curName = typeof cur.name === "string" && cur.name.trim() ? cur.name : "Customer";
  const current = {
    slot: Math.trunc(Number(cur.slot ?? 0) || 0),
    name: curName,
    discountPersen: Number(cur.discountPersen ?? 0) || 0,
  };

  let next: LevelProgressNext | null = null;
  if (obj.next && typeof obj.next === "object") {
    const n = obj.next as Record<string, unknown>;
    const window: PromoteWindow =
      n.window === "calendar_month" ||
      n.window === "calendar_year" ||
      n.window === "rolling_days"
        ? (n.window as PromoteWindow)
        : "all_time";
    next = {
      slot: Math.trunc(Number(n.slot ?? 0) || 0),
      name: typeof n.name === "string" ? n.name : "",
      discountPersen: Number(n.discountPersen ?? 0) || 0,
      match: n.match === "any" ? "any" : "all",
      window,
      windowDays: Math.max(1, Math.trunc(Number(n.windowDays ?? 30) || 30)),
      minSpending: Math.max(0, Number(n.minSpending ?? 0) || 0),
      minOrders: Math.max(0, Math.trunc(Number(n.minOrders ?? 0) || 0)),
      spending: Math.max(0, Number(n.spending ?? 0) || 0),
      orderCount: Math.max(0, Math.trunc(Number(n.orderCount ?? 0) || 0)),
    };
  }

  return { current, next, isMax: !!obj.isMax || next === null };
}
